import { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import { Save, Plus, X, RotateCcw, Settings as SettingsIcon } from "lucide-react";

const FIELDS = [
  { key: "nationalities", label: "Customer nationalities", placeholder: "e.g. European" },
  { key: "current_products", label: "Current product used", placeholder: "e.g. Vape" },
  { key: "heard_about_ubbs", label: "Heard about UBBS — radio options", placeholder: "e.g. yes" },
  { key: "nicotine_levels", label: "Nicotine levels", placeholder: "e.g. 11mg to 14mg" },
];

const EMPTY = {
  nationalities: [],
  current_products: [],
  heard_about_ubbs: [],
  nicotine_levels: [],
};

function ListEditor({ field, items, onChange }) {
  const [draft, setDraft] = useState("");
  const add = () => {
    const v = draft.trim();
    if (!v) return;
    if (items.includes(v)) {
      toast.error("Already in the list");
      return;
    }
    onChange([...items, v]);
    setDraft("");
  };
  const remove = (i) => {
    const next = items.slice();
    next.splice(i, 1);
    onChange(next);
  };
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const next = items.slice();
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  return (
    <div className="glass-panel p-4">
      <div className="flex items-baseline justify-between mb-3 gap-3">
        <h3 className="font-display font-bold text-lg tracking-tight">{field.label}</h3>
        <span className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
          {items.length} option{items.length === 1 ? "" : "s"}
        </span>
      </div>
      <div className="flex gap-2 mb-3">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
          placeholder={field.placeholder}
          className="rounded-xl border-slate-300/70"
          data-testid={`survey-options-${field.key}-input`}
        />
        <Button
          type="button"
          onClick={add}
          className="rounded-xl bg-slate-900 text-white hover:bg-slate-700"
          data-testid={`survey-options-${field.key}-add`}
        >
          <Plus className="h-4 w-4 mr-1" /> Add
        </Button>
      </div>
      {items.length === 0 ? (
        <div className="text-sm text-neutral-500 font-mono uppercase tracking-wider py-4 text-center">
          Empty — add at least one option
        </div>
      ) : (
        <ul className="divide-y divide-slate-200/50 glass-panel">
          {items.map((v, i) => (
            <li
              key={`${v}-${i}`}
              className="flex items-center justify-between px-3 py-2 hover:bg-white/50"
              data-testid={`survey-options-${field.key}-item-${i}`}
            >
              <span className="text-sm">{v}</span>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => move(i, -1)}
                  disabled={i === 0}
                  className="px-2 py-1 font-mono text-xs text-neutral-500 hover:text-black disabled:opacity-30"
                  aria-label="Move up"
                >
                  ↑
                </button>
                <button
                  type="button"
                  onClick={() => move(i, 1)}
                  disabled={i === items.length - 1}
                  className="px-2 py-1 font-mono text-xs text-neutral-500 hover:text-black disabled:opacity-30"
                  aria-label="Move down"
                >
                  ↓
                </button>
                <button
                  type="button"
                  onClick={() => remove(i)}
                  className="px-2 py-1 text-red-600 hover:bg-red-50"
                  aria-label="Remove"
                  data-testid={`survey-options-${field.key}-remove-${i}`}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function AdminSurveyOptions() {
  const [data, setData] = useState(EMPTY);
  const [initial, setInitial] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/survey-options");
      const safe = { ...EMPTY, ...r.data };
      setData(safe);
      setInitial(safe);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const dirty = JSON.stringify(data) !== JSON.stringify(initial);

  const save = async () => {
    for (const f of FIELDS) {
      if (!data[f.key] || data[f.key].length === 0) {
        toast.error(`${f.label} cannot be empty`);
        return;
      }
    }
    setSaving(true);
    try {
      const r = await api.put("/admin/survey-options", data);
      const safe = { ...EMPTY, ...r.data };
      setData(safe);
      setInitial(safe);
      toast.success("Survey options saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-survey-options-page">
      <div className="flex items-baseline justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2">
            <SettingsIcon className="h-4 w-4 text-neutral-500" />
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">
              Survey configuration
            </span>
          </div>
          <h1 className="font-display font-semibold text-3xl tracking-tight">
            Survey options
          </h1>
          <p className="text-sm text-neutral-600 mt-1 max-w-2xl">
            Dropdown values used by the promoter Survey form. Reorder with ↑↓, remove with ✕,
            press Enter to add. Changes appear on the promoter app on next page load.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            className="rounded-xl"
            onClick={load}
            disabled={loading || saving}
            data-testid="survey-options-reload"
          >
            <RotateCcw className="h-4 w-4 mr-1" /> Reload
          </Button>
          <Button
            className="rounded-xl bg-slate-900 text-white hover:bg-slate-700 disabled:opacity-40"
            onClick={save}
            disabled={!dirty || saving || loading}
            data-testid="survey-options-save"
          >
            <Save className="h-4 w-4 mr-1" /> {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-neutral-500 font-mono uppercase tracking-wider">
          Loading…
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {FIELDS.map((f) => (
            <ListEditor
              key={f.key}
              field={f}
              items={data[f.key] || []}
              onChange={(arr) => setData((d) => ({ ...d, [f.key]: arr }))}
            />
          ))}
        </div>
      )}
    </div>
  );
}
